import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Trash2, Loader2 } from 'lucide-react';
import {
  formatDisplayPrice,
  calculateBasePrice,
  calculateItemSubtotal,
} from 'utils/price';
import { useSafeExecutor } from 'hooks/useErrorHandler';
import QuantityStepper from 'components/ui/QuantityStepper';
import useViewport from 'hooks/useViewport';

// Constants
const CONSTANTS = {
  MAX_QUANTITY: 99,
  MIN_QUANTITY: 1,
  ICON_SIZE: 20,
  TOUCH_TARGET_SIZE: 44, // Minimum touch target size
};

// Container for an individual cart item - Mobile first approach
const CartItem = styled.li`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.greyLight};
  opacity: ${({ $isUpdating }) => ($isUpdating ? 0.7 : 1)};
  transition: opacity 200ms ease;
  gap: 12px;

  /* Tablet and up: More spacing */
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 16px 20px;
    gap: 16px;
  }

  /* Desktop: Even more spacious */
  ${({ theme }) => theme.mediaQueries.lg} {
    padding: 20px 24px;
  }
`;

// Container for the main item details
const ItemMainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ theme }) => theme.mediaQueries.md} {
    gap: 12px;
  }
`;

// Top section - Mobile: stacked, Desktop: side-by-side with price
const ItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  /* Tablet and up: horizontal layout */
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
`;

// Item name styling - Mobile first typography
const ItemName = styled.h3`
  ${({ theme }) => theme.mixins.textP1};
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.3;

  /* Tablet: Slightly larger */
  ${({ theme }) => theme.mediaQueries.md} {
    ${({ theme }) => theme.mixins.textH4};
    line-height: 1.2;
  }

  /* Desktop: Even larger and more refined */
  ${({ theme }) => theme.mediaQueries.lg} {
    ${({ theme }) => theme.mixins.textH3};
  }
`;

// Quantity and base price display - Mobile optimized
const ItemQuantity = styled.span`
  ${({ theme }) => theme.mixins.textP3};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey};

  /* Tablet and up: Larger text */
  ${({ theme }) => theme.mediaQueries.md} {
    ${({ theme }) => theme.mixins.textP2};
    font-weight: 700;
  }
`;

// Container for item modifiers/add-ons - Mobile first spacing
const ModifiersWrapper = styled.div`
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid ${({ theme }) => theme.colors.greyLight};

  ${({ theme }) => theme.mediaQueries.md} {
    margin-top: 4px;
    padding-left: 16px;
    border-left-width: 3px;
  }
`;

// Individual modifier line item - Mobile optimized spacing
const ModifierItem = styled.div`
  ${({ theme }) => theme.mixins.textP3};
  color: ${({ theme }) => theme.colors.green};
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  min-height: 20px;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-bottom: 2px;
    min-height: 24px;
  }
`;

// Modifier name display
const ModifierName = styled.span`
  flex: 1;
  margin-right: 8px;
`;

// Modifier price display
const ModifierPrice = styled.span`
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 0.85em;
  font-weight: 600;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 0.9em;
  }
`;

// Plus symbol for add-on modifiers
const ModifierSymbol = styled.span`
  ${({ theme }) => theme.mixins.textP3};
  color: ${({ theme }) => theme.colors.green};
  font-weight: 700;
`;

// Container for modifier price and symbol
const ModifierPriceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 50px;
  justify-content: flex-end;

  ${({ theme }) => theme.mediaQueries.md} {
    gap: 6px;
    min-width: 60px;
  }
`;

// Container for controls - Mobile: stacked, Desktop: horizontal
const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;

  /* Tablet and up: horizontal layout */
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    gap: 16px;
  }
`;

// Groups the quantity stepper and remove button - Mobile optimized
const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  /* Tablet and up: Normal spacing */
  ${({ theme }) => theme.mediaQueries.md} {
    justify-content: flex-start;
    gap: 16px;
  }
`;

// Button to remove item from cart - Mobile first with proper touch targets
const RemoveButton = styled.button`
  background: none;
  border: 2px solid transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  padding: 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: ${CONSTANTS.TOUCH_TARGET_SIZE}px;
  min-height: ${CONSTANTS.TOUCH_TARGET_SIZE}px;

  /* Mobile: Always show background for better visibility */
  background-color: ${({ theme }) => theme.colors.backgroundLight || '#f8f9fa'};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Mobile: Active state for touch */
  &:active:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryLight || '#e3f2fd'};
    transform: scale(0.98);
  }

  /* Tablet and up: Hover states and refined styling */
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 8px;
    background-color: transparent;
    border-radius: 4px;
    min-width: auto;
    min-height: auto;

    &:hover:not(:disabled) {
      color: ${({ theme }) => theme.colors.primaryDark};
      background-color: ${({ theme }) =>
        theme.colors.backgroundLight || '#f5f5f5'};
      border-color: ${({ theme }) => theme.colors.greyLight};
    }

    &:active:not(:disabled) {
      transform: none;
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

// Item total price display - Mobile first sizing
const ItemPrice = styled.span`
  ${({ theme }) => theme.mixins.textP1};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.backgroundLight || '#f8f9fa'};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.greyLight};

  /* Tablet and up: More refined appearance */
  ${({ theme }) => theme.mediaQueries.md} {
    ${({ theme }) => theme.mixins.textP2};
    text-align: right;
    padding: 4px 8px;
    background-color: transparent;
    border: none;
    border-radius: 0;
  }

  /* Desktop: Larger text */
  ${({ theme }) => theme.mediaQueries.lg} {
    ${({ theme }) => theme.mixins.textH4};
  }
`;

// Live region for screen reader updates (hidden from visual users)
const LiveRegion = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

/**
 * ItemComponent displays an individual cart item with details and controls
 * Now fully mobile-first and responsive
 */
const ItemComponent = React.memo(
  ({ item, onIncrement, onDecrement, onRemove }) => {
    const { isMobile, isTablet } = useViewport();
    const safeExecute = useSafeExecutor('ItemComponent', { itemId: item.id });

    // Loading states for operations
    const [loadingStates, setLoadingStates] = useState({
      incrementing: false,
      decrementing: false,
      removing: false,
    });

    // Live region content for screen reader updates
    const [liveMessage, setLiveMessage] = useState('');

    // Check if any operation is in progress
    const isUpdating = useMemo(() => {
      return Object.values(loadingStates).some(Boolean);
    }, [loadingStates]);

    // Helper to update loading state
    const setLoadingState = useCallback((operation, isLoading) => {
      setLoadingStates((prev) => ({ ...prev, [operation]: isLoading }));
    }, []);

    // Helper to announce changes to screen readers
    const announceChange = useCallback((message) => {
      setLiveMessage(message);
      setTimeout(() => setLiveMessage(''), 1000);
    }, []);

    // Handlers with loading states
    const handleIncrement = useCallback(async () => {
      if (isUpdating) return;

      setLoadingState('incrementing', true);
      try {
        await safeExecute(() => onIncrement(item), 'increment');
        announceChange(
          `Increased ${item.name} quantity to ${item.quantity + 1}`,
        );
      } finally {
        setLoadingState('incrementing', false);
      }
    }, [
      item,
      onIncrement,
      safeExecute,
      isUpdating,
      setLoadingState,
      announceChange,
    ]);

    const handleDecrement = useCallback(async () => {
      if (isUpdating) return;

      setLoadingState('decrementing', true);
      try {
        await safeExecute(() => onDecrement(item), 'decrement');
        const newQuantity = item.quantity - 1;
        if (newQuantity > 0) {
          announceChange(`Decreased ${item.name} quantity to ${newQuantity}`);
        }
      } finally {
        setLoadingState('decrementing', false);
      }
    }, [
      item,
      onDecrement,
      safeExecute,
      isUpdating,
      setLoadingState,
      announceChange,
    ]);

    const handleRemove = useCallback(async () => {
      if (isUpdating) return;

      setLoadingState('removing', true);
      try {
        await safeExecute(() => onRemove(item), 'remove');
        announceChange(`Removed ${item.name} from cart`);
      } finally {
        setLoadingState('removing', false);
      }
    }, [
      item,
      onRemove,
      safeExecute,
      isUpdating,
      setLoadingState,
      announceChange,
    ]);

    // Computed values memoized
    const formattedBasePrice = useMemo(() => {
      return safeExecute(
        () => {
          const basePrice = calculateBasePrice(item);
          return formatDisplayPrice(basePrice);
        },
        'formatBasePrice',
        '—',
      );
    }, [item, safeExecute]);

    const formattedSubtotal = useMemo(() => {
      return safeExecute(
        () => {
          const subtotal = calculateItemSubtotal(item);
          return formatDisplayPrice(subtotal);
        },
        'formatSubtotal',
        '—',
      );
    }, [item, safeExecute]);

    const hasModifiers = useMemo(() => {
      return safeExecute(
        () => {
          return (
            item.modifiers &&
            Array.isArray(item.modifiers) &&
            item.modifiers.length > 0
          );
        },
        'checkHasModifiers',
        false,
      );
    }, [item, safeExecute]);

    // Determine QuantityStepper variant based on screen size
    const stepperVariant = useMemo(() => {
      if (isMobile) return 'large';
      if (isTablet) return 'medium';
      return 'small';
    }, [isMobile, isTablet]);

    /**
     * Safely process and render modifiers (array format only)
     */
    const renderModifiers = useCallback(() => {
      return safeExecute(
        () => {
          if (
            !item.modifiers ||
            !Array.isArray(item.modifiers) ||
            item.modifiers.length === 0
          ) {
            return null;
          }

          return item.modifiers.map((mod, idx) => {
            const modifierName = mod.name || '';
            const modifierPrice = mod.price || 0;

            if (modifierPrice <= 0) {
              return (
                <ModifierItem key={`modifier-${idx}`}>
                  <ModifierName>{modifierName}</ModifierName>
                </ModifierItem>
              );
            }

            const totalModifierPrice = formatDisplayPrice(
              modifierPrice * item.quantity,
            );
            return (
              <ModifierItem key={`modifier-${idx}`}>
                <ModifierName>{modifierName}</ModifierName>
                <ModifierPriceWrapper>
                  <ModifierSymbol>+</ModifierSymbol>
                  <ModifierPrice>{totalModifierPrice}</ModifierPrice>
                </ModifierPriceWrapper>
              </ModifierItem>
            );
          });
        },
        'renderModifiers',
        null,
      );
    }, [item, safeExecute]);

    return (
      <CartItem $isUpdating={isUpdating}>
        <ItemMainContent>
          <ItemHeader>
            <ItemName>{item.name}</ItemName>
            <ItemQuantity
              aria-label={`Quantity: ${item.quantity}, Unit price: ${formattedBasePrice}`}
            >
              {item.quantity} × {formattedBasePrice}
            </ItemQuantity>
          </ItemHeader>

          {hasModifiers && (
            <ModifiersWrapper role="list" aria-label="Item modifications">
              {renderModifiers()}
            </ModifiersWrapper>
          )}

          <ActionsWrapper>
            <ControlsGroup>
              <QuantityStepper
                value={item.quantity}
                onIncrement={handleIncrement}
                onDecrement={handleDecrement}
                min={CONSTANTS.MIN_QUANTITY}
                max={CONSTANTS.MAX_QUANTITY}
                itemName={item.name}
                variant={stepperVariant}
                disabled={isUpdating}
              />

              <RemoveButton
                type="button"
                onClick={handleRemove}
                disabled={isUpdating}
                aria-label={`Remove ${item.name} from cart`}
                title={isUpdating ? 'Please wait...' : `Remove ${item.name}`}
              >
                {loadingStates.removing ? (
                  <Loader2
                    size={CONSTANTS.ICON_SIZE}
                    className="animate-spin"
                  />
                ) : (
                  <Trash2 size={CONSTANTS.ICON_SIZE} />
                )}
              </RemoveButton>
            </ControlsGroup>

            <ItemPrice
              aria-label={`Total price: ${formattedSubtotal}`}
              role="status"
            >
              {formattedSubtotal}
            </ItemPrice>
          </ActionsWrapper>
        </ItemMainContent>

        {/* Live region for screen reader announcements */}
        <LiveRegion role="status" aria-live="polite" aria-atomic="true">
          {liveMessage}
        </LiveRegion>
      </CartItem>
    );
  },
);

ItemComponent.displayName = 'ItemComponent';

ItemComponent.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    subtotal: PropTypes.number,
    price: PropTypes.number,
    basePrice: PropTypes.number,
    baseItem: PropTypes.shape({ price: PropTypes.number }),
    originalItem: PropTypes.shape({
      basePrice: PropTypes.number,
      price: PropTypes.number,
    }),
    modifiers: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        price: PropTypes.number,
      }),
    ),
  }).isRequired,
  onIncrement: PropTypes.func.isRequired,
  onDecrement: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ItemComponent;
